// BL-2355: the lot label's QR link — one canonical, house-independent URL per
// environment (`<env bidder base>/qr/<lot id>`, Michael Lawrie 2026-10-09).
// Pure (ADR 010 — Isomorphic Services): the admin label builder and bidder-web's
// `/qr` forwarder share it, so the printed link and the route can't drift.

import { normalizeBidderOrigin } from '../notifications/bidder-origin';

/** The path prefix the Admin app claims on its environment's bidder host. */
export const LOT_QR_PATH = '/qr';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Build a lot's canonical QR link.
 *
 * Always the environment's bidder host (`NEXT_PUBLIC_BIDDER_BASE_URL`), never a
 * house's own domain, so one claimed host per app build covers every house.
 *
 * @param input.bidderBaseUrl - the env bidder base URL, e.g. `https://bidding-dev.auctogon.com`.
 * @param input.lotId - the `auction_lots.id`.
 * @returns `<base>/qr/<lotId>`, or null when the base is missing or not http(s) — never a relative URL.
 */
export function buildLotQrUrl(input: { bidderBaseUrl: string | null | undefined; lotId: string }): string | null {
  const origin = normalizeBidderOrigin(input.bidderBaseUrl);
  if (!/^https?:\/\/[^/:]+(:\d+)?$/i.test(origin)) return null;
  return `${origin}${LOT_QR_PATH}/${encodeURIComponent(input.lotId)}`;
}

/**
 * Whether a `/qr/<id>` segment is a well-formed lot id (a uuid).
 *
 * @param value - the raw path segment.
 * @returns true for a uuid, in any case.
 */
export function isLotQrId(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * Read a lot id back out of a scanned label link: the inverse of `buildLotQrUrl` (BL-2416).
 *
 * The Admin app hands every link it is opened with to the web code; only this environment's
 * label link may route anywhere. Another host (another environment, a house domain), the native
 * sign-in callback, or any path but exactly `/qr/<uuid>` is ignored.
 *
 * @param input.url - the URL the app was opened with.
 * @param input.bidderBaseUrl - the env bidder base URL (`NEXT_PUBLIC_BIDDER_BASE_URL`).
 * @returns the lot id, or null when the URL is not this environment's label link.
 */
export function parseLotQrLink(input: { url: string; bidderBaseUrl: string | null | undefined }): string | null {
  const origin = normalizeBidderOrigin(input.bidderBaseUrl);
  if (!origin) return null;
  let parsed: URL;
  try {
    parsed = new URL(input.url);
  } catch {
    return null;
  }
  if (parsed.origin !== origin) return null;
  const match = new RegExp(`^${LOT_QR_PATH}/([^/]+)$`).exec(parsed.pathname);
  const id = match?.[1] ?? null;
  return id && isLotQrId(id) ? id : null;
}
